import { useMemo } from "react";

import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";

/**
 * The lead desk (feat/room-to-revenue, docs/room-to-revenue.md): every lead as
 * a card on a pipeline and as a file, follow-ups written by Ardi, and the sales
 * Ardealy influenced. Routes live here rather than in assets/constants so the
 * workstreams adding pages tonight do not all edit one file.
 */
const BASE = "/services/pms/lead-desk";

export const LEAD_DESK_STAGES = ["new", "contacted", "sample", "visit", "quote", "won", "lost"] as const;
export type LeadDeskStage = (typeof LEAD_DESK_STAGES)[number];
export type LeadTier = "hot" | "warm" | "cold";
export type ContactChannel = "whatsapp" | "email" | "phone" | "visit";
export type FollowUpChannel = "whatsapp" | "email";

export interface Money {
    amount: number;
    currency: string;
}

export interface DeskCard {
    id: string;
    email: string;
    name: string | null;
    phone: string | null;
    stage: LeadDeskStage;
    stageAt: string;
    tier: LeadTier;
    score: number;
    reasons: string[];
    roomImageUrl: string | null;
    products: Array<{ productId: string; title: string; thumbnailPath: string | null }>;
    productCount: number;
    areaM2: number | null;
    value: (Money & { source: string }) | null;
    lastSeenAt: string | null;
    lastActiveAt: string | null;
    lastContactAt: string | null;
    nextStepAt: string | null;
    ownerUserId: string | null;
    audience: "consumer" | "trade";
    requestKinds: string[];
}

export interface DeskList {
    data: DeskCard[];
    counts: {
        byStage: Record<LeadDeskStage, number>;
        byTier: Record<LeadTier, number>;
        byAudience: { consumer: number; trade: number };
    };
    kpis: {
        hotWaiting: number;
        pipelineValue: (Money & { leads: number }) | null;
        wonThisMonth: { count: number; amount: number | null; currency: string | null };
    };
    lockedCount: number;
    pagination: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean };
}

export interface DeskState {
    stage: LeadDeskStage;
    stageAt: string;
    ownerUserId: string | null;
    value: (Money & { source: string }) | null;
    nextStepAt: string | null;
    lastContactAt: string | null;
    lastContactChannel: ContactChannel | null;
    notes: Array<{ at: string; by?: string; text: string }>;
    contacts: Array<{ at: string; channel: ContactChannel; by?: string; summary?: string }>;
    lostReason: string | null;
    wonAt: string | null;
    wonAmount: number | null;
}

export interface LeadFileMessage {
    role: "visitor" | "agent";
    text: string;
    at: string;
    products: Array<{ uuid: string; name: string; image: string | null; price: string | null }>;
    photo: { spaceId: string; imageUrl: string | null } | null;
    render: { spaceId: string; productUuid: string; surface: string } | null;
}

export interface LeadFileRequest {
    uuid: string;
    kind: string;
    status: string;
    createdAt: string | null;
    items: Array<{ productUuid: string; title?: string; thumbnailUrl?: string; areaM2?: number; quantity?: number; unit?: string; boxes?: number; unitPrice?: number; total?: number }>;
    total: number | null;
    currency: string | null;
    areaM2: number | null;
    areaRange: [number, number] | null;
    store: { id?: string; name: string; address?: string; distanceKm?: number } | null;
    preferredDate: string | null;
    preferredSlot: string | null;
    message: string | null;
    source: string | null;
    lang: string | null;
}

export interface TimelineEntry {
    at: string;
    kind: string;
    count: number;
    productIds?: string[];
    detail?: string;
}

export interface LeadFile {
    id: string;
    email: string;
    contact: { name?: string; phone?: string; preferred?: "whatsapp" | "email" | "phone"; email: string };
    consent: { marketing: boolean; unsubscribedAt: string | null };
    audience: "consumer" | "trade";
    firstSeenAt: string | null;
    lastSeenAt: string | null;
    eventCount: number;
    attribution: Record<string, any> | null;
    score: { value: number; tier: LeadTier; reasons: string[]; computedAt: string; lastActiveAt: string | null };
    desk: DeskState;
    images: {
        renders: Array<{ url: string; source: string; at: string | null }>;
        rooms: Array<{ url: string; source: string; at: string | null }>;
    };
    products: Array<{
        productId: string;
        title: string;
        sku: string | null;
        thumbnailPath: string | null;
        link: string | null;
        usageCount: number;
        lastUsedAt: string | null;
        price: { amount: number; currency: string; unit?: string; compareAt?: number } | null;
    }>;
    area: { m2: number; range: [number, number] | null; source: string | null } | null;
    value: (Money & { source: string }) | null;
    nearestStore: {
        name: string;
        address?: string | null;
        city?: string;
        distanceKm: number | null;
        hours?: string;
        phone?: string;
        bookingUrl?: string;
        basis: "request" | "visit";
    } | null;
    visitorLocation: { country: string | null; countryCode: string | null; city: string | null } | null;
    requests: LeadFileRequest[];
    threads: Array<{ uuid: string; startedAt: string | null; lastMessageAt: string | null; language: string | null; pageUrl: string | null; messages: LeadFileMessage[] }>;
    timeline: TimelineEntry[];
    activity: { downloads: number; emailsSent: number; lastEmailedAt: string | null; activeDays30: number };
    vendor: { name: string | null; language: string | null };
}

export interface DeskPatch {
    stage?: LeadDeskStage;
    ownerUserId?: string | null;
    value?: Money | null;
    nextStepAt?: string | null;
    note?: string;
    lostReason?: string | null;
    wonAmount?: number | null;
    contact?: { name?: string; phone?: string; preferred?: "whatsapp" | "email" | "phone" };
}

export interface FollowUpDraft {
    channel: FollowUpChannel;
    lang: "fr" | "en" | "es" | "ar";
    subject: string | null;
    text: string;
    html: string | null;
    waLink: string | null;
    phone: string | null;
    phoneResolved: boolean;
    to: string | null;
    renderUrl: string | null;
}

export interface InfluenceReport {
    from: string;
    to: string;
    currency: string | null;
    influencedRevenue: number;
    influencedOrders: number;
    totalRevenue: number;
    totalOrders: number;
    share: number | null;
    byMonth: Array<{ month: string; influencedRevenue: number; influencedOrders: number; totalRevenue: number; totalOrders: number }>;
    planMonthly: (Money & { source: "contract" | "wire" | "offer" | "plan" }) | null;
    months: number;
    planCost: number | null;
    roi: number | null;
    roiApprox: boolean;
    method: { windowDays: number; viaPixel: number; viaEmail: number; unchecked: number; pixel: boolean; emailMatch: boolean };
    setup: {
        shopConnected: boolean;
        platform: string | null;
        shopDomain: string | null;
        customersGrant: boolean;
        lastSyncAt: string | null;
        checkoutsReported: number;
        lastCheckoutAt: string | null;
        lastCheckoutSource: string | null;
        sdkVisitors: number;
    };
    unmatchedCheckouts: { count: number; reportedTotal: number | null };
    orders: Array<{
        id: string;
        orderNumber: string | null;
        placedAt: string;
        total: number;
        currency: string | null;
        channel: string | null;
        via: "pixel" | "email";
        leadId: string | null;
        leadEmail: string | null;
        firstTouchAt: string | null;
        daysToOrder: number | null;
        products: Array<{ title: string | null; productUuid: string | null; quantity: number | null }>;
    }>;
}

export interface PixelSnippet {
    endpoint: string;
    vendorUuid: string;
    shopify: string;
}

const query = (params: Record<string, string | number | undefined | null>) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
    });
    const text = search.toString();
    return text ? `?${text}` : "";
};

export const useLeadDeskApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    return useMemo(() => ({
        list: async (params: { stage?: string; tier?: string; audience?: string; q?: string; page?: number; limit?: number } = {}) =>
            await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/leads${query(params)}`) as DeskList,

        file: async (id: string) =>
            await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/leads/${encodeURIComponent(id)}`) as LeadFile,

        patchDesk: async (id: string, patch: DeskPatch) =>
            await sendAuthGuardedRequest(ApiMethod.PATCH, `${BASE}/leads/${encodeURIComponent(id)}/desk`, patch) as DeskState,

        draftFollowUp: async (id: string, body: { channel: FollowUpChannel; lang?: string }) =>
            await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/leads/${encodeURIComponent(id)}/follow-up/draft`, body) as FollowUpDraft,

        markSent: async (id: string, body: { channel: ContactChannel; text?: string }) =>
            await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/leads/${encodeURIComponent(id)}/follow-up/sent`, body) as DeskState,

        influence: async (params: { from?: string; to?: string } = {}) =>
            await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/influence${query(params)}`) as InfluenceReport,

        pixel: async () => await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/pixel`) as PixelSnippet,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [sendAuthGuardedRequest]);
};
